import { useMemo } from 'react'
import { Link } from 'react-router'
import { ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { TagBadge } from '@/components/tag/tag-badge'
import { ColorDot } from '@/components/ui/color-dot'
import { formatShortDate, formatAmount, getTransactionDisplayName } from '@/lib/utils'
import { UNCATEGORIZED_LABEL } from '@/lib/constants'

export type TransactionSummary = {
  id: string
  date: string
  store_name: string | null
  total_amount: number
  input_method: string
  paid_by_name: string | null
  paid_by_avatar_url?: string | null
  scope?: 'household' | 'personal'
  items: { name: string; amount: number; category_name?: string | null; parent_category_name?: string | null; parent_category_color?: string | null }[]
  tags?: Array<{ id: string; name: string; color: string }>
}

type TransactionListItemProps = {
  transaction: TransactionSummary
  to: string
  showDate?: boolean
  overrideAmount?: number
}

type CategorySegment = { parent: string | null; child: string; color: string | null }

function buildCategorySegments(items: TransactionSummary['items']): CategorySegment[] {
  const map = new Map<string, CategorySegment & { total: number }>()
  for (const item of items) {
    const key = item.parent_category_name && item.category_name
      ? `${item.parent_category_name}>${item.category_name}`
      : item.category_name ?? '__uncategorized__'
    const existing = map.get(key)
    if (existing) {
      existing.total += item.amount
    } else {
      map.set(key, {
        parent: item.parent_category_name ?? null,
        child: item.category_name ?? UNCATEGORIZED_LABEL,
        color: item.parent_category_color ?? null,
        total: item.amount,
      })
    }
  }
  return [...map.values()].sort((a, b) => b.total - a.total)
}

export function TransactionListItem({ transaction: tx, to, showDate = true, overrideAmount }: TransactionListItemProps) {
  const displayDate = formatShortDate(tx.date)
  const displayName = getTransactionDisplayName(tx)
  const categorySegments = useMemo(() => buildCategorySegments(tx.items), [tx.items])

  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-accent"
    >
      <div className="flex items-center gap-3 min-w-0 overflow-hidden">
        {showDate && (
          <span className="shrink-0 text-sm text-muted-foreground w-10">
            {displayDate}
          </span>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate text-sm font-medium">{displayName}</span>
            {tx.input_method === 'recurring' && (
              <Badge variant="secondary" className="shrink-0 text-xs">
                固定
              </Badge>
            )}
            {tx.scope === 'personal' && (
              <Badge variant="outline" className="shrink-0 text-xs">
                個人
              </Badge>
            )}
          </div>
          <span className="flex items-center gap-1 text-xs text-muted-foreground truncate">
            {categorySegments.map((seg, i) => (
              <span key={i} className="flex items-center gap-1">
                <ColorDot color={seg.color} />
                {seg.child}
              </span>
            ))}
          </span>
          {tx.tags && tx.tags.length > 0 && (
            <span className="flex items-center gap-1">
              {tx.tags.slice(0, 2).map(tag => (
                <TagBadge key={tag.id} name={tag.name} color={tag.color} size="sm" />
              ))}
              {tx.tags.length > 2 && (
                <span className="text-xs text-muted-foreground">+{tx.tags.length - 2}</span>
              )}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-sm font-medium">
          {formatAmount(overrideAmount ?? tx.total_amount)}
        </span>
        <Avatar className="size-5 shrink-0">
          {tx.paid_by_avatar_url && (
            <AvatarImage src={tx.paid_by_avatar_url} alt={tx.paid_by_name ?? ''} />
          )}
          <AvatarFallback className="text-[10px]">
            {tx.paid_by_name?.charAt(0) ?? '?'}
          </AvatarFallback>
        </Avatar>
        <ChevronRight className="size-4 text-muted-foreground" />
      </div>
    </Link>
  )
}
